import React, { useEffect, useMemo, useState } from "react";
import { Card, Typography, Row, Col } from "antd";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase/config";
import MetricsCards from "../components/dashboard/MetricsCards";
import MetricsChart from "../components/dashboard/MetricsChart";
import MetricsTable from "../components/dashboard/MetricsTable";
import PageFilter from "../components/dashboard/PageFilter";
import ChannelChart from "../components/dashboard/ChannelChart";
import ChannelTable from "../components/dashboard/ChannelTable";

const { Title } = Typography;

export default function Dashboard() {
  const [dailyData, setDailyData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState("all");

  async function loadDashboardData() {
    try {
      setLoading(true);

      const dailyQuery = query(collection(db, "daily_metrics"), orderBy("date", "asc"));
      const dailySnapshot = await getDocs(dailyQuery);

      const dailyRows = dailySnapshot.docs.map((docItem) => {
        const item = docItem.data();

        return {
          key: docItem.id,
          date: item.date,
          pageViews: item.pageViews || 0,
          uniqueVisitors: item.uniqueVisitors || 0,
          sessions: item.sessions || 0,
          whatsappClicks: item.whatsappClicks || 0,
          totalTimeSeconds: item.totalTimeSeconds || 0,
          totalTimeEvents: item.totalTimeEvents || 0,
          avgTimeOnSite:
            item.totalTimeEvents > 0
              ? Math.round(item.totalTimeSeconds / item.totalTimeEvents)
              : 0,
          whatsappCtr:
            item.pageViews > 0
              ? Number((((item.whatsappClicks || 0) / item.pageViews) * 100).toFixed(2))
              : 0,
        };
      });

      const eventsQuery = query(collection(db, "events"), orderBy("createdAt", "asc"));
      const eventsSnapshot = await getDocs(eventsQuery);

      const eventRows = eventsSnapshot.docs.map((docItem) => {
        const item = docItem.data();

        return {
          key: docItem.id,
          event: item.event || "",
          page: item.page || "",
          date: item.date || "",
          visitorId: item.visitorId || "",
          sessionId: item.sessionId || "",
          utm_source: item.utm_source || "sem_utm",
          utm_medium: item.utm_medium || "",
          utm_campaign: item.utm_campaign || "",
          utm_content: item.utm_content || "",
          utm_term: item.utm_term || "",
          durationSeconds: item.durationSeconds || 0,
        };
      });

      setDailyData(dailyRows);
      setEventsData(eventRows);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboardData();
  }, []);

  const pageOptions = useMemo(() => {
    const pages = eventsData
      .map((item) => item.page)
      .filter(Boolean);

    return [...new Set(pages)];
  }, [eventsData]);

  const filteredEvents = useMemo(() => {
    if (selectedPage === "all") {
      return eventsData;
    }

    return eventsData.filter((item) => item.page === selectedPage);
  }, [eventsData, selectedPage]);

  const dateMetrics = useMemo(() => {
    const grouped = {};

    filteredEvents.forEach((item) => {
      if (!item.date) return;

      if (!grouped[item.date]) {
        grouped[item.date] = {
          key: item.date,
          date: item.date,
          pageViews: 0,
          uniqueVisitorsSet: new Set(),
          sessionsSet: new Set(),
          whatsappClicks: 0,
          totalTimeSeconds: 0,
          totalTimeEvents: 0,
        };
      }

      if (item.event === "page_view") {
        grouped[item.date].pageViews += 1;
        if (item.visitorId) grouped[item.date].uniqueVisitorsSet.add(item.visitorId);
      }

      if (item.event === "session_start") {
        if (item.sessionId) grouped[item.date].sessionsSet.add(item.sessionId);
      }

      if (item.event === "whatsapp_click") {
        grouped[item.date].whatsappClicks += 1;
      }

      if (item.event === "time_on_page") {
        grouped[item.date].totalTimeSeconds += Number(item.durationSeconds || 0);
        grouped[item.date].totalTimeEvents += 1;
      }
    });

    return Object.values(grouped)
      .map((item) => {
        const avgTimeOnSite =
          item.totalTimeEvents > 0
            ? Math.round(item.totalTimeSeconds / item.totalTimeEvents)
            : 0;

        const whatsappCtr =
          item.pageViews > 0
            ? Number(((item.whatsappClicks / item.pageViews) * 100).toFixed(2))
            : 0;

        return {
          key: item.key,
          date: item.date,
          pageViews: item.pageViews,
          uniqueVisitors: item.uniqueVisitorsSet.size,
          sessions: item.sessionsSet.size,
          whatsappClicks: item.whatsappClicks,
          totalTimeSeconds: item.totalTimeSeconds,
          totalTimeEvents: item.totalTimeEvents,
          avgTimeOnSite,
          whatsappCtr,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEvents]);

  const totals = useMemo(() => {
    return dateMetrics.reduce(
      (acc, item) => {
        acc.pageViews += item.pageViews;
        acc.uniqueVisitors += item.uniqueVisitors;
        acc.sessions += item.sessions;
        acc.whatsappClicks += item.whatsappClicks;
        acc.totalTimeSeconds += item.totalTimeSeconds || 0;
        acc.totalTimeEvents += item.totalTimeEvents || 0;
        return acc;
      },
      {
        pageViews: 0,
        uniqueVisitors: 0,
        sessions: 0,
        whatsappClicks: 0,
        totalTimeSeconds: 0,
        totalTimeEvents: 0,
      }
    );
  }, [dateMetrics]);

  const avgTime =
    totals.totalTimeEvents > 0
      ? Math.round(totals.totalTimeSeconds / totals.totalTimeEvents)
      : 0;

  const totalCtr =
    totals.pageViews > 0
      ? Number(((totals.whatsappClicks / totals.pageViews) * 100).toFixed(2))
      : 0;

  const channelData = useMemo(() => {
    const grouped = {};

    filteredEvents.forEach((item) => {
      const source = item.utm_source || "sem_utm";

      if (!grouped[source]) {
        grouped[source] = {
          key: source,
          source,
          pageViews: 0,
          sessionsSet: new Set(),
          whatsappClicks: 0,
        };
      }

      if (item.event === "page_view") {
        grouped[source].pageViews += 1;
      }

      if (item.event === "session_start" && item.sessionId) {
        grouped[source].sessionsSet.add(item.sessionId);
      }

      if (item.event === "whatsapp_click") {
        grouped[source].whatsappClicks += 1;
      }
    });

    return Object.values(grouped)
      .map((item) => {
        const sessions = item.sessionsSet.size;
        const whatsappCtr =
          item.pageViews > 0
            ? Number(((item.whatsappClicks / item.pageViews) * 100).toFixed(2))
            : 0;

        return {
          key: item.key,
          source: item.source,
          pageViews: item.pageViews,
          sessions,
          whatsappClicks: item.whatsappClicks,
          whatsappCtr,
        };
      })
      .sort((a, b) => b.whatsappClicks - a.whatsappClicks);
  }, [filteredEvents]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Dashboard de Performance</Title>

      <PageFilter
        pageOptions={pageOptions}
        selectedPage={selectedPage}
        onChange={setSelectedPage}
      />

      <MetricsCards
        metrics={{
          pageViews: totals.pageViews,
          uniqueVisitors: totals.uniqueVisitors,
          sessions: totals.sessions,
          whatsappClicks: totals.whatsappClicks,
          avgTimeOnSite: avgTime,
          whatsappCtr: totalCtr,
        }}
      />

      <div style={{ marginTop: 24 }}>
        <Card title="Evolução por data">
          <MetricsChart data={dateMetrics} />
        </Card>
      </div>

      <div style={{ marginTop: 24 }}>
        <Card title="Resumo diário">
          <MetricsTable data={dateMetrics} loading={loading} />
        </Card>
      </div>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Conversão por canal">
            <ChannelChart data={channelData} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Tabela por canal">
            <ChannelTable data={channelData} loading={loading} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}